import type {
    CreateThemeTokens,
    CreateThemeKey,
    CreateSettings,
    CreatePalette
} from "./settings";
import themeJSON from "../../../src/variants/themes/default.json";

type CodeValue = string | Array<CodeValue> | CodeObject;

type CodeObject = {
    [key: string]: CodeValue;
};

type TokenTree = {
    [key: string]: TokenTree | string;
};

type ShippedPalette = {
    themes: Record<CreateThemeKey, CreateThemeTokens>;
    name: string;
};

const PALETTE_NAME = "my-brand";

const THEMES_IDENTIFIER = "myBrandThemes";

const THEMES_TYPE = "NCoreUIKit.PartialTheme";

const REFERENCE_PALETTE_NAME = "nibgat";

const INDENT = "    ";

const IDENTIFIER_PATTERN = /^[A-Za-z_$][\w$]*$/;

const SHIPPED_PALETTES = themeJSON.palettes as unknown as Array<ShippedPalette>;

const THEME_KEYS: Array<CreateThemeKey> = [
    "light",
    "dark"
];

const getReferenceTheme = (themeKey: CreateThemeKey) => {
    const palette = SHIPPED_PALETTES.find((item) => item.name === REFERENCE_PALETTE_NAME) ?? SHIPPED_PALETTES[0]!;

    return palette.themes[themeKey] as TokenTree;
};

const getChangedTokens = (tokens: TokenTree, reference: TokenTree | undefined): CodeObject => {
    const changed: CodeObject = {};

    Object.entries(tokens).forEach(([
        key,
        value
    ]) => {
        const referenceValue = reference?.[key];

        if(typeof value === "string") {
            if(value !== referenceValue) {
                changed[key] = value;
            }

            return;
        }

        const nested = getChangedTokens(value, typeof referenceValue === "string" ? undefined : referenceValue);

        if(Object.keys(nested).length > 0) {
            changed[key] = nested;
        }
    });

    return changed;
};

const formatKey = (key: string) => {
    return IDENTIFIER_PATTERN.test(key) ? key : JSON.stringify(key);
};

const formatString = (value: string) => {
    return value === THEMES_IDENTIFIER ? value : JSON.stringify(value);
};

const renderValue = (value: CodeValue, depth: number): Array<string> => {
    const indent = INDENT.repeat(depth);

    if(typeof value === "string") {
        return [
            formatString(value)
        ];
    }

    if(Array.isArray(value)) {
        if(value.length === 0) {
            return [
                "[]"
            ];
        }

        const items = value.map((item) => renderValue(item, depth + 1));

        return [
            "[",
            ...items.flatMap((lines, index) => withComma(lines, index < items.length - 1).map((line, lineIndex) => lineIndex === 0 ? `${indent}${INDENT}${line}` : line)),
            `${indent}]`
        ];
    }

    const properties = Object.entries(value).map(([
        key,
        item
    ]) => {
        const lines = renderValue(item, depth + 1);

        const firstLine = `${formatKey(key)}: ${lines[0] ?? ""}`;

        const propertyLines = [
            firstLine,
            ...lines.slice(1)
        ];

        return {
            length: Math.max(...propertyLines.map((line, index) => index === 0 ? `${indent}${INDENT}${line}`.length : line.length)),
            text: propertyLines.join("\n"),
            lines: propertyLines
        };
    });

    if(properties.length === 0) {
        return [
            "{}"
        ];
    }

    properties.sort((first, second) => second.length - first.length || first.text.localeCompare(second.text));

    return [
        "{",
        ...properties.flatMap((property, index) => withComma(property.lines, index < properties.length - 1).map((line, lineIndex) => lineIndex === 0 ? `${indent}${INDENT}${line}` : line)),
        `${indent}}`
    ];
};

const withComma = (lines: Array<string>, hasComma: boolean) => {
    if(!hasComma) {
        return lines;
    }

    return lines.map((line, index) => index === lines.length - 1 ? `${line},` : line);
};

const getChangedThemes = (palette: CreatePalette): CodeObject => {
    const themes: CodeObject = {};

    THEME_KEYS.forEach((themeKey) => {
        themes[themeKey] = getChangedTokens(palette.themes[themeKey] as TokenTree, getReferenceTheme(themeKey));
    });

    return themes;
};

const getProjectThemes = (settings: CreateSettings): CodeObject | null => {
    const projectThemes: CodeObject = {};

    if(settings.preset === "custom") {
        projectThemes.palettes = [
            {
                themes: THEMES_IDENTIFIER,
                name: PALETTE_NAME
            }
        ];
    }

    if(settings.cornerShape === "round") {
        projectThemes.shapes = {
            shapeConfigs: {
                cornerShape: settings.cornerShape
            }
        };
    }

    return Object.keys(projectThemes).length > 0 ? projectThemes : null;
};

export const getProjectThemesCode = (settings: CreateSettings, palette: CreatePalette) => {
    const isCustom = settings.preset === "custom";

    const projectThemes = getProjectThemes(settings);

    const options: CodeObject = {
        initialSelectedPalette: isCustom ? PALETTE_NAME : settings.preset,
        initialSelectedActiveSharpness: settings.sharpness,
        initialSelectedGapPropagation: settings.density,
        initialSelectedTheme: settings.themeKey
    };

    if(projectThemes) {
        options.projectThemes = projectThemes;
    }

    const lines = [
        "import {",
        `${INDENT}setupNCoreUIKit`,
        "} from \"ncore-ui-kit\";",
        ""
    ];

    if(isCustom) {
        lines.push(
            "declare global {",
            `${INDENT}namespace NCoreUIKit {`,
            `${INDENT}${INDENT}interface PaletteKey extends Record<"${PALETTE_NAME}", unknown> {}`,
            `${INDENT}}`,
            "}",
            ""
        );

        const themeLines = renderValue(getChangedThemes(palette), 0);

        lines.push(
            `const ${THEMES_IDENTIFIER}: ${THEMES_TYPE} = ${themeLines[0] ?? "{"}`,
            ...themeLines.slice(1, -1),
            `${themeLines[themeLines.length - 1] ?? "}"};`,
            ""
        );
    }

    const optionLines = renderValue(options, 0);

    lines.push(
        `const NCoreUIKitBase = setupNCoreUIKit(${optionLines[0] ?? "{"}`,
        ...optionLines.slice(1, -1),
        `${optionLines[optionLines.length - 1] ?? "}"});`
    );

    return lines.join("\n");
};
